import { useCallback, useState } from 'react'

import {
  NotificationContext,
  NotificationStatus,
  readNotificationStatus,
} from './notification-status.js'

interface NotificationStatusValue {
  status: NotificationStatus
  context: NotificationContext | null
}

export interface NotificationStatusState extends NotificationStatusValue {
  /** Re-reads the live permission state, updates status/context, returns the new value. */
  recompute: () => NotificationStatusValue
}

/**
 * Owns the permission status + context. Initial state is a neutral, SSR-safe
 * default; the consumer recomputes it on mount (client-only) to avoid a
 * hydration mismatch.
 */
export const useNotificationStatus = (): NotificationStatusState => {
  const [status, setStatus] = useState<NotificationStatus>('default')
  const [context, setContext] = useState<NotificationContext | null>(null)

  const recompute = useCallback((): NotificationStatusValue => {
    const next = readNotificationStatus()
    setStatus(next.status)
    setContext(next.context)
    return next
  }, [])

  return { status, context, recompute }
}
